import React, { useState, useEffect, useMemo } from 'react';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import { useDateFormatter, useLanguageDirection } from '@/shared/hooks';
import { ChooseListInput } from '@/shared/components/inputs/ChooseList.input';
import { DatePickerInput } from '@/shared/components/inputs/DatePicker.input';
import { WizardStepProps } from '../types/wizard.types';
import { ComponentReturnTypeMap } from '@momentum/types';

type DateChoiceValue = 'today' | 'yesterday' | 'custom';

interface WizardDateChoiceStepProps
  extends WizardStepProps<ComponentReturnTypeMap['DateSelection']> {
  maxDate?: Date;
}

export const WizardDateChoiceStep: React.FC<WizardDateChoiceStepProps> = ({
  value,
  onChange,
  onNext,
  error,
  disabled,
  maxDate,
}) => {
  const { t } = useTranslation('common');
  const { formatDate } = useDateFormatter();
  const { isRTL } = useLanguageDirection();

  const [selectedChoice, setSelectedChoice] =
    useState<DateChoiceValue>('today');
  const [customDate, setCustomDate] = useState<Date | null>(null);

  // Calculate dates
  const today = useMemo(() => new Date(), []);
  const yesterday = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date;
  }, []);

  // Initialize choice based on value prop
  useEffect(() => {
    if (!value) {
      setSelectedChoice('today');
      return;
    }

    const valueDate = new Date(value);
    const todayDate = new Date();
    const yesterdayDate = new Date(todayDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);

    // Compare dates (ignoring time)
    const isSameDay = (date1: Date, date2: Date) => {
      return date1.toDateString() === date2.toDateString();
    };

    if (isSameDay(valueDate, todayDate)) {
      setSelectedChoice('today');
    } else if (isSameDay(valueDate, yesterdayDate)) {
      setSelectedChoice('yesterday');
    } else {
      setSelectedChoice('custom');
      setCustomDate(valueDate);
    }
  }, [value]);

  // Handle choice change
  const handleChoiceChange = (choice: string) => {
    const dateChoice = choice as DateChoiceValue;
    setSelectedChoice(dateChoice);

    let selectedDate: Date | null = null;

    switch (dateChoice) {
      case 'today':
        selectedDate = today;
        break;
      case 'yesterday':
        selectedDate = yesterday;
        break;
      case 'custom':
        selectedDate = customDate;
        break;
    }

    if (selectedDate) {
      onChange(selectedDate);
    }

    // Only auto-advance if we have a valid date and it's different from current value
    // This prevents auto-advancing when switching to "Another Date" mode
    if (selectedDate && onNext && selectedDate !== value) {
      setTimeout(() => {
        onNext(selectedDate);
      }, 100); // Small delay for better UX
    }
  };

  // Handle custom date selection
  const handleCustomDateChange = (date: Date | null) => {
    setCustomDate(date);
    if (selectedChoice === 'custom' && date !== null) {
      onChange(date);

      // Auto-advance when custom date is selected
      if (date && onNext && date !== value) {
        setTimeout(() => {
          onNext(date);
        }, 100);
      }
    }
  };

  // Create options for ChooseListInput
  const options = useMemo(
    () => [
      {
        id: 'today',
        title: t('today', 'Today'),
        description: formatDate(today, 'medium'),
        icon: <CalendarIcon className="w-6 h-6 text-primary" />,
      },
      {
        id: 'yesterday',
        title: t('yesterday', 'Yesterday'),
        description: formatDate(yesterday, 'medium'),
        icon: <CalendarIcon className="w-6 h-6 text-primary" />,
      },
      {
        id: 'custom',
        title: t('anotherDate', 'Another date'),
        description:
          selectedChoice === 'custom' && customDate
            ? formatDate(customDate, 'medium')
            : undefined,
        icon: <CalendarIcon className="w-6 h-6 text-primary" />,
      },
    ],
    [t, formatDate, today, yesterday, selectedChoice, customDate]
  );

  return (
    <div className={cn('w-full space-y-4')} dir={isRTL ? 'rtl' : 'ltr'}>
      <ChooseListInput
        options={options}
        value={selectedChoice}
        onChange={handleChoiceChange}
        required={false}
        disabled={disabled}
        variant="card"
        className="space-y-3"
      />

      {/* Date Picker for Custom Option */}
      {selectedChoice === 'custom' && (
        <div className="mt-3">
          <DatePickerInput
            value={customDate}
            onChange={handleCustomDateChange}
            placeholder={t('selectDate', 'Select date')}
            disabled={disabled}
            maxDate={maxDate}
            error={error}
          />
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};
